import * as React from 'react'
import { API, OrderType, RemoteResource, ResourceType, ResourcesOrdering } from '../../services/api'
import { isTimelineOrderBy } from '../GallerySort/gallerySortParams'
import { buildTimelineIndex, TimelineIndex } from './timelineIndex'

export interface ResourceGalleryFilters {
  albumId?: string
  publicPhotos?: boolean
  tags?: Array<string>
  personIds?: Array<string>
  ownerIds?: Array<string>
  deviceId?: string
  placeId?: string
  type?: ResourceType
  dateFrom?: Date
  dateTo?: Date
  isFavorite?: boolean
  // ordering is part of the result set's identity, so it belongs in filtersKey
  orderBy?: ResourcesOrdering
  order?: OrderType
}

export interface ScrollTarget {
  flatIndex: number
  token: number
}

export type WindowedMode = 'pending' | 'virtual' | 'fallback'

export interface BucketJumpTarget {
  /** timeline bucket key ("YYYY-MM" for scrubber month jumps) */
  key: string
  /** flat index of the bucket's first item, from timeline prefix sums */
  itemsBefore: number
}

interface UseWindowedResourcesOptions {
  enabled: boolean
  workspaceId: string
  filters: ResourceGalleryFilters
  pageSize?: number
  maxWindowItems?: number
  /** while true (photo preview open) the window only grows, so indices stay valid */
  holdTrim?: boolean
}

interface UseWindowedResourcesResult {
  mode: WindowedMode
  timeline: TimelineIndex | null
  /** contiguous loaded window; items[0] is flat index windowStart */
  items: Array<RemoteResource>
  windowStart: number
  total: number
  isFetching: boolean
  allFetched: boolean
  ensureRange: (startFlat: number, endFlat: number) => void
  jumpToFlatIndex: (target: number) => void
  /** far jumps land exactly on the bucket boundary (no partial first day) */
  jumpToBucket: (bucket: BucketJumpTarget) => void
  scrollTarget: ScrollTarget | null
  clearScrollTarget: () => void
  reset: () => void
}

interface Snapshot {
  mode: WindowedMode
  timeline: TimelineIndex | null
  items: Array<RemoteResource>
  windowStart: number
  total: number
  isFetching: boolean
  scrollTarget: ScrollTarget | null
}

const INITIAL: Snapshot = {
  mode: 'pending',
  timeline: null,
  items: [],
  windowStart: 0,
  total: 0,
  isFetching: false,
  scrollTarget: null,
}

const DEFAULT_PAGE_SIZE = 100
const DEFAULT_MAX_WINDOW_ITEMS = 600

const filtersKey = (filters: ResourceGalleryFilters): string =>
  JSON.stringify(filters, (_, value) => (value === undefined ? null : value))

export const useWindowedResources = ({
  enabled,
  workspaceId,
  filters,
  pageSize = DEFAULT_PAGE_SIZE,
  maxWindowItems = DEFAULT_MAX_WINDOW_ITEMS,
  holdTrim = false,
}: UseWindowedResourcesOptions): UseWindowedResourcesResult => {
  const [snapshot, setSnapshot] = React.useState<Snapshot>(INITIAL)

  const stateRef = React.useRef<Snapshot>(INITIAL)
  const desiredRef = React.useRef<{ start: number; end: number } | null>(null)
  const farJumpRef = React.useRef<{ target: number; bucket?: string } | null>(null)
  const workerRef = React.useRef(false)
  const generationRef = React.useRef(0)
  const tokenRef = React.useRef(0)
  const holdTrimRef = React.useRef(holdTrim)
  holdTrimRef.current = holdTrim

  const commit = (patch: Partial<Snapshot>) => {
    stateRef.current = { ...stateRef.current, ...patch }
    setSnapshot(stateRef.current)
  }

  const filterKey = filtersKey(filters)
  const filtersRef = React.useRef(filters)
  filtersRef.current = filters

  const timezone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])

  const fetchPage = (from: number, bucket?: string) =>
    API.getInstance().fetchResources({
      workspaceId,
      from,
      count: pageSize,
      bucket,
      timezone: bucket ? timezone : undefined,
      albumId: filtersRef.current.albumId,
      public: filtersRef.current.publicPhotos,
      tags: filtersRef.current.tags,
      personIds: filtersRef.current.personIds,
      ownerIds: filtersRef.current.ownerIds,
      deviceId: filtersRef.current.deviceId,
      placeId: filtersRef.current.placeId,
      type: filtersRef.current.type,
      dateFrom: filtersRef.current.dateFrom,
      dateTo: filtersRef.current.dateTo,
      isFavorite: filtersRef.current.isFavorite,
      // bucket slices derive their date source from order_by, so it must go out here too
      orderBy: filtersRef.current.orderBy,
      order: filtersRef.current.order,
    })

  const alignDown = (index: number): number => Math.floor(index / pageSize) * pageSize

  const trimIfNeeded = (desired: { start: number; end: number } | null) => {
    const { items, windowStart } = stateRef.current
    if (holdTrimRef.current || items.length <= maxWindowItems) return

    const excessPages = Math.floor((items.length - maxWindowItems) / pageSize) + 1
    const keepStart = desired ? alignDown(Math.max(0, desired.start)) : windowStart
    const keepEnd = desired ? desired.end : windowStart + items.length

    // drop whole pages from whichever side is farther from the desired range
    const canDropTop = Math.max(0, Math.floor((keepStart - windowStart) / pageSize))
    const canDropBottom = Math.max(
      0,
      Math.floor((windowStart + items.length - keepEnd) / pageSize)
    )
    const dropTop = Math.min(excessPages, canDropTop)
    const dropBottom = Math.min(excessPages - dropTop, canDropBottom)

    if (dropTop === 0 && dropBottom === 0) return
    const next = items.slice(
      dropTop * pageSize,
      dropBottom > 0 ? items.length - dropBottom * pageSize : undefined
    )
    commit({ items: next, windowStart: windowStart + dropTop * pageSize })
  }

  const runWorker = async () => {
    if (workerRef.current) return
    workerRef.current = true
    const generation = generationRef.current

    try {
      while (generation === generationRef.current) {
        const { items, windowStart, total, mode } = stateRef.current
        if (mode !== 'virtual') break

        let from: number | null = null
        let action: 'replace' | 'append' | 'prepend' = 'append'

        const farTarget = farJumpRef.current
        if (farTarget !== null) {
          // bucket jumps fetch the bucket's own page 0; its global offset is farTarget.target
          from = farTarget.bucket ? 0 : alignDown(farTarget.target)
          action = 'replace'
        } else {
          const desired = desiredRef.current
          if (!desired) break
          const coveredStart = windowStart
          const coveredEnd = windowStart + items.length
          const start = Math.max(0, desired.start)
          const end = Math.min(total, desired.end)

          if (items.length === 0) {
            from = alignDown(start)
            action = 'replace'
          } else if (end > coveredEnd && coveredEnd < total) {
            from = coveredEnd
            action = 'append'
          } else if (start < coveredStart) {
            from = Math.max(0, coveredStart - pageSize)
            action = 'prepend'
          } else {
            break
          }
        }

        commit({ isFetching: true })
        const page = await fetchPage(from, farTarget?.bucket)
        if (generation !== generationRef.current) return

        // a failed fetch returns an empty page — bail without touching state or treating it as
        // end-of-list, leaving desiredRef intact so the next scroll / sentinel retries this range
        if (!page.ok) {
          commit({ isFetching: false })
          break
        }

        const current = stateRef.current
        // pagination headers may not be CORS-exposed; keep the previous total then.
        // A bucket fetch counts only the bucket, never the whole library.
        const pageTotal = farTarget?.bucket ? current.total : page.count || current.total
        if (action === 'replace') {
          commit({
            items: page.elements,
            // bucket fetches use bucket-local offsets, so the global start is the boundary itself
            windowStart: farTarget?.bucket ? farTarget.target : Math.min(from, pageTotal),
            total: pageTotal,
            isFetching: false,
          })
          // clear only if no newer jump has replaced it meanwhile
          if (farJumpRef.current === farTarget) {
            farJumpRef.current = null
          }
          // an empty replace means there is nothing at the target — stop chasing it
          if (page.elements.length === 0) desiredRef.current = null
        } else if (action === 'append') {
          commit({
            items: current.items.concat(page.elements),
            total: pageTotal,
            isFetching: false,
          })
        } else {
          commit({
            items: page.elements.concat(current.items),
            windowStart: from,
            total: pageTotal,
            isFetching: false,
          })
        }

        // a short page means the end of the list — stop chasing an unreachable range
        if (action !== 'replace' && page.elements.length < pageSize) {
          const { windowStart: ws, items: its } = stateRef.current
          if (desiredRef.current) {
            desiredRef.current = {
              start: Math.max(0, desiredRef.current.start),
              end: Math.min(desiredRef.current.end, ws + its.length),
            }
          }
        }

        trimIfNeeded(desiredRef.current)
      }
    } finally {
      workerRef.current = false
      if (stateRef.current.isFetching) commit({ isFetching: false })
    }
  }

  const ensureRange = (startFlat: number, endFlat: number) => {
    if (stateRef.current.mode !== 'virtual') return
    const total = stateRef.current.total
    if (total === 0) return
    const pad = Math.floor(pageSize / 2)
    desiredRef.current = {
      start: Math.max(0, startFlat - pad),
      end: Math.min(total, endFlat + pad),
    }
    runWorker()
  }

  const jumpTo = (target: number, bucket?: string) => {
    const { mode, total, windowStart, items } = stateRef.current
    if (mode !== 'virtual' || total === 0) return
    const clamped = Math.max(0, Math.min(target, total - 1))
    const coveredStart = windowStart
    const coveredEnd = windowStart + items.length

    tokenRef.current += 1
    commit({ scrollTarget: { flatIndex: clamped, token: tokenRef.current } })

    const isFar = clamped < coveredStart - pageSize || clamped >= coveredEnd + pageSize
    if (isFar) {
      farJumpRef.current = { target: clamped, bucket }
      desiredRef.current = null
      runWorker()
    } else {
      ensureRange(clamped, clamped + pageSize)
    }
  }

  const jumpToFlatIndex = (target: number) => jumpTo(target)

  const jumpToBucket = (bucket: BucketJumpTarget) => jumpTo(bucket.itemsBefore, bucket.key)

  const clearScrollTarget = () => {
    if (stateRef.current.scrollTarget !== null) commit({ scrollTarget: null })
  }

  const initialize = async () => {
    generationRef.current += 1
    const generation = generationRef.current
    desiredRef.current = null
    farJumpRef.current = null
    stateRef.current = INITIAL
    setSnapshot(INITIAL)

    if (!enabled) {
      commit({ mode: 'fallback' })
      return
    }

    commit({ isFetching: true })
    const [timelineData, firstPage] = await Promise.all([
      API.getInstance().fetchResourceTimeline({
        workspaceId,
        timezone,
        albumId: filtersRef.current.albumId,
        public: filtersRef.current.publicPhotos,
        tags: filtersRef.current.tags,
        personIds: filtersRef.current.personIds,
        ownerIds: filtersRef.current.ownerIds,
        deviceId: filtersRef.current.deviceId,
        placeId: filtersRef.current.placeId,
        type: filtersRef.current.type,
        dateFrom: filtersRef.current.dateFrom,
        dateTo: filtersRef.current.dateTo,
        isFavorite: filtersRef.current.isFavorite,
        // the endpoint takes the narrower union and has no `order` — virtual mode is
        // only enabled for orderings it accepts, so this never silently mismatches
        orderBy: isTimelineOrderBy(filtersRef.current.orderBy)
          ? filtersRef.current.orderBy
          : undefined,
      }),
      fetchPage(0),
    ])
    if (generation !== generationRef.current) return

    if (!timelineData) {
      commit({ mode: 'fallback', isFetching: false })
      return
    }

    commit({
      mode: 'virtual',
      timeline: buildTimelineIndex(timelineData),
      items: firstPage.elements,
      windowStart: 0,
      // header may not be CORS-exposed; the timeline body carries the same total
      total: firstPage.count || timelineData.total,
      isFetching: false,
    })
  }

  React.useEffect(() => {
    initialize()
    return () => {
      generationRef.current += 1
    }
  }, [enabled, workspaceId, filterKey, pageSize])

  return {
    mode: snapshot.mode,
    timeline: snapshot.timeline,
    items: snapshot.items,
    windowStart: snapshot.windowStart,
    total: snapshot.total,
    isFetching: snapshot.isFetching,
    allFetched: snapshot.windowStart + snapshot.items.length >= snapshot.total,
    ensureRange,
    jumpToFlatIndex,
    jumpToBucket,
    scrollTarget: snapshot.scrollTarget,
    clearScrollTarget,
    reset: initialize,
  }
}
